/*
 * In these utils, we deal primarily with 3 types of DOM entities:
 * - Node: The base type for any entity
 * - Element: Extends Node, and is the type for any HTML element in general
 * - Text: Extends Node, and is the type for an HTML node for text in the DOM
 *
 * For example:
 * <div>Testing</div>
 *
 * In this example, we have a div, which is an Element, and inside it is a Text node with the text
 * "Testing". Both of these entities extend Node, but neither extends the other (and they are not
 * exchangable). It is important to understand this distinction when working with these utils.
 * Whenever we want to obtain the text contents in a token element, we do so by finding its Text
 * child, and reading the data from that (as opposed to using something like innerText on the
 * Element itself). Furthermore, when a variable could store an Element or a Text node, it may make
 * sense to type it as a Node (parent type).
 */

import { create } from '@thoughtspot/logger';
import cx from 'classnames';
import DOMPurify from 'dompurify';
import _ from 'lodash';
import { Key } from 'w3c-keys';
import { navigationalKeys } from '/@utils/event-utils';
import { CaretPosition } from '/@utils/sage.utils';
import { SINGLE_WHITESPACE_REGEX } from '/@utils/tokenization.util';
import { TokenizerToken, TokenizerTokenPosition } from './tokenizer.config';
import {
    getKeydownSpecialCaseHandler,
    moveCaretIntoNextNodeStart,
} from './tokenizer.crossbrowser.util';
import styles from './tokenizer.module.scss';
import { isEmptyTokenOrSeparator } from './tokenizer.shared.util';

// Only exported so we can spy on it during tests
export const logger = create('Tokenizer.Util');

export * from './tokenizer.shared.util';
export * from './tokenizer.crossbrowser.util';

// We use these as an initial filter during keyboared events to determine if we can just ignore
// the key or not. If the key is not in this set, we will ignore the key.
export const keysWhichNeedProcessing = new Set([
    Key.Space,
    Key.Backspace,
    Key.Delete,
    Key.Enter,
    Key.Escape,
    ...navigationalKeys,
]);

// Bounds for the delete button
const TOKEN_DELETE_BUTTON_BOUNDS = {
    top: 9, // Offset from top edge (+ve means more up)
    right: 6, // Offset from right edge (+ve means more to the right)
    bottom: 10, // Offset from top edge (+ve means more down)
    left: 11, // Offset from right edge (+ve means more to the left)
};

// Bounds for the context menu button
const TOKEN_CONTEXT_MENU_BUTTON_BOUNDS = {
    top: 0, // Offset from top edge (+ve means more up)
    right: 0, // Offset from right edge (+ve means more to the right)
    bottom: 24, // Offset from top edge (+ve means more down)
    left: 22, // Offset from right edge (+ve means more to the left)
};

// The height in pixels of a token (including all paddings, borders, and margins)
export const TOKEN_HEIGHT_PX = 32;

// TODO(Rifdhan) do we need all of them?
export const classes = {
    IS_CONTEXT_MENU_ENABLED: styles.isContextMenuEnabled,
    SEPARATOR: styles.separator,
    TOKEN: styles.token,
    EDITED: styles.bkEditedNode,
    INCOMPLETE: styles.incomplete,
    PENDING: styles.pending,
    DELETED: styles.deleted,
    EXTENSIBLE: styles.extensible,
    CONTEXT_MENU_OPENED: styles.contextMenuOpened,
    TOKENIZER_CONTAINER: styles.tokenizerContainer,
    INIT_TOKEN: styles.initToken,
    DISABLE_HOVER_EFFECTS: styles.disableHoverEffects,
    DISABLE_ANIMATIONS: styles.disableAnimations,
};

export const initTokenTemplate = `<div class="${classes.INIT_TOKEN}"></div>`;

export const getTokenTemplate = (
    id: string,
    text: string,
    tooltip: string,
    cssClasses: string,
): string => {
    return `<div data-token-id="${id}" data-tooltip-content="${tooltip}" data-tooltip-position="bottom" data-tooltip-align="left" class="${cssClasses}">${text}</div>`;
};

export const separatorTemplate = `<div class="${classes.SEPARATOR}"></div>`;

export const keydownSpecialCaseHandler: (
    container: Element,
    evt: KeyboardEvent,
) => void = getKeydownSpecialCaseHandler();

export function isNodeEmpty(node: Element | undefined): boolean {
    if (!node) {
        // TODO(Rifdhan) true?
        return false;
    }
    return node.textContent === '';
}

function getTextChild(node: Node): Text {
    if (node.nodeName === '#text') {
        return node as Text;
    }

    return getTextChild(node.firstChild as Node);
}

export function isSeparator(node: Element): boolean {
    // TODO(Rifdhan) remove extraneous checks
    return (
        node &&
        node.classList &&
        (node.classList.contains(classes.SEPARATOR) ||
            node.classList.contains(classes.INIT_TOKEN))
    );
}

/*
 * Returns a list of all elements that contain text. This typically includes only token elements,
 * but it's possible it will also include separators, if the user has just typed something into a
 * separator, and we have't yet updated it into a token.
 */
function getTokenElements(tokenizerElement: Element): Element[] {
    return Array.from(tokenizerElement.children).filter(
        (tokenElement: Element) => {
            // This is meant to exclude empty separators from the final list
            return tokenElement.textContent.trim().length > 0;
        },
    );
}

/*
 * Finds the element containing the caret, and the char offset of the caret inside that element
 */
export function getCaretTokenAndCharOffset(): [HTMLElement, number] {
    const range = document.getSelection().getRangeAt(0);
    if (!range) {
        logger.warn(
            'Trying to get caret token but no selection ranges detected',
        );
        return [null, 0];
    }

    const rangeEndElement =
        range.endContainer.nodeName === '#text'
            ? range.endContainer.parentElement
            : (range.endContainer as HTMLElement);

    const charOffsetInCaretToken = range.endOffset;
    // A small sanity check, can remove if this is not happening
    if (charOffsetInCaretToken > rangeEndElement.textContent.length) {
        logger.warn(
            'Char offset in caret token is past end of caret token text contents',
            rangeEndElement,
            rangeEndElement.textContent,
            charOffsetInCaretToken,
        );
    }

    return [rangeEndElement, charOffsetInCaretToken];
}

/**
 * Returns a boolean representing whether the specified element
 * contains the whole of the user's selection
 * source: https://stackoverflow.com/a/8340432/3248247
 */
function elementContainsSelection(el: Element) {
    const sel = document.getSelection();

    // if empty selection, it should return false
    if (sel.toString() === '') return false;
    if (sel.rangeCount > 0) {
        for (let i = 0; i < sel.rangeCount; ++i) {
            if (!el.contains(sel.getRangeAt(i).commonAncestorContainer)) {
                return false;
            }
        }
        return true;
    }
    return false;
}

/*
 * Determines if the caret is at the end of a token that is complete. If the token is extensible,
 * the caller can specify whether that counts as incomplete. Also checks if there is a text
 * range selection in which case it does not count as caret being at the end.
 */
export function isCaretAtEndOfCompleteToken(
    markAsIncompleteIfIsExtensible = true,
): boolean {
    const [caretToken, charOffsetInCaretToken] = getCaretTokenAndCharOffset();

    // If the caret is not inside a token, then the caret is obviously not at the end of a complete
    // token, which is still a valid scenario we consider here
    if (!caretToken) {
        return false;
    }

    const isCaretAtEndOfToken =
        charOffsetInCaretToken === caretToken.textContent.length;

    // If the caret token is extensible, we consider it incomplete if the caller told us to do so
    const incompleteDueToExtensibility: boolean =
        markAsIncompleteIfIsExtensible &&
        caretToken.classList.contains(classes.EXTENSIBLE);

    return (
        isCaretAtEndOfToken &&
        !incompleteDueToExtensibility &&
        caretToken.classList.contains(classes.TOKEN) &&
        !caretToken.classList.contains(classes.INCOMPLETE) &&
        !elementContainsSelection(caretToken)
    );
}

export function isRangeSelection(): boolean {
    const selection = document.getSelection();
    return !selection.isCollapsed;
}

export function isCaretInContainer(container: Element): boolean {
    const selection = document.getSelection();
    const anchorNode: Node =
        (selection as any).baseNode || selection.anchorNode;
    return (
        container.contains(anchorNode) ||
        container.contains(anchorNode.parentNode)
    );
}

// TODO(Rifdhan) noticed problems with this if we remove the _.isEqual(newTokens, oldTokens) check
// before token application in the state hook
export function getCaretPositionInCompleteInput(
    tokenizerElement: Element,
): CaretPosition {
    const selection = document.getSelection();
    if (selection.rangeCount === 0) {
        logger.warn(
            'getCaretPositionInCompleteInput called with no selection present',
        );
        return {
            caretCharIdx: 0,
            isCaretInSeparator: false,
        };
    }

    // Find the element where the end of the range is (we want to find the token element, so if we
    // got a text element, its parent should be the token element we want)
    const range = selection.getRangeAt(0);
    let rangeEndElement: Element = (range.endContainer.nodeName === '#text'
        ? range.endContainer.parentNode
        : range.endContainer) as Element;

    // If the range ends on an empty token/separator, scan until we find the first non-empty token,
    // or until we run out of tokens. If we run out of tokens, rangeEndElement will be null, which
    // means the caret was in the separator at the very end of all the tokens, and we will scan
    // over all the tokens in the for loop below this, to find the absolute caret position.
    let rangeEndsOnEmptySeparator = false;
    while (rangeEndElement && isEmptyTokenOrSeparator(rangeEndElement)) {
        rangeEndsOnEmptySeparator = true;
        rangeEndElement = rangeEndElement.nextElementSibling;
    }

    // Iterate through the token elements and accumulate the total length of text up to (but not
    // including) the element which contains the end of the range
    const tokenElements: Element[] = getTokenElements(tokenizerElement);
    let textLengthUpToCaretToken = 0;
    for (let childIdx = 0; childIdx < tokenElements.length; childIdx++) {
        // Get the current token and stop scanning if we found the token where the range ends
        const currentTokenElement: Element = tokenElements[childIdx];
        if (currentTokenElement === rangeEndElement) {
            break;
        }

        // Add the length of text in this token element to the cumulative total (adding one for the
        // separator at the end, which represents a space)
        textLengthUpToCaretToken +=
            (currentTokenElement.textContent as string).length + 1;
    }

    // If the cursor was in a separator, we would have counted up to the token after that
    // separator, including counting one extra space for the separator itself - thus we should
    // subtract one character to go "back" to the separator (see notes on how separator caret
    // positions are represented in the CaretPosition object)
    if (rangeEndsOnEmptySeparator) {
        textLengthUpToCaretToken = Math.max(textLengthUpToCaretToken - 1, 0);
    }

    return {
        // Adding these two gives the absolute char offset of the caret within the full token text
        caretCharIdx: textLengthUpToCaretToken + range.endOffset,
        // TODO(Rifdhan) clean up distinction between tokens/separators
        isCaretInSeparator: rangeEndsOnEmptySeparator,
    };
}

// TODO(Rifdhan) merge with below?
export function moveCaretIntoStartOfNode(node: Node) {
    const range = document.createRange();
    range.setStart(node, 0);
    const selection = document.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
}

export function moveCaretIntoNodeWithOffset(node: Node, offset = 0) {
    const textChild: Text = getTextChild(node);
    const range = document.createRange();
    range.setStart(textChild, offset);
    const selection = document.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
}

export function moveCaretToPosition(
    tokenizerElement: Element,
    caretPosition: CaretPosition,
) {
    // If there are no tokens, place the caret in the first node (this is the case if the query is
    // empty, so we want to place the caret in the init token). If somehow no child nodes exist,
    // place the caret in the container itself. TODO(Rifdhan): is that last case ever possible?
    const tokenElements: Element[] = getTokenElements(tokenizerElement);
    if (tokenElements.length === 0) {
        moveCaretIntoStartOfNode(
            tokenizerElement.firstChild || tokenizerElement,
        );
        return;
    }

    // Iterate through all tokens until we find the one where the caret should be inserted in
    let currentCharOffset = 0;
    for (let childIdx = 0; childIdx < tokenElements.length; childIdx++) {
        const currentTokenElement: Element = tokenElements[childIdx];
        if (
            currentCharOffset + currentTokenElement.textContent.length >=
            caretPosition.caretCharIdx
        ) {
            // The caret should be inserted into the current token. If the caret will end up at the
            // boundary at the end of the current token, check whether it should actually be moved
            // out into the separator after this token instead.
            if (
                currentCharOffset + currentTokenElement.textContent.length ===
                    caretPosition.caretCharIdx &&
                caretPosition.isCaretInSeparator
            ) {
                // Move out to separator after the current token
                moveCaretIntoStartOfNode(
                    currentTokenElement.nextElementSibling,
                );
            } else {
                // If the caret was in a separator before but not anymore, account for that by
                // moving the caret forward one character position. This happens if the user moved
                // their caret out of an incomplete phrase, but the sage response tells us to treat
                // it as starting to add a new token at the end of the same phrase, so their caret
                // moves back into the phrase, but with a new space after the previous last token.
                const charOffsetInToken =
                    caretPosition.caretCharIdx - currentCharOffset;
                const caretPositionAdjustment =
                    caretPosition.isCaretInSeparator &&
                    charOffsetInToken < currentTokenElement.textContent.length
                        ? 1
                        : 0;

                moveCaretIntoNodeWithOffset(
                    currentTokenElement,
                    charOffsetInToken + caretPositionAdjustment,
                );
            }

            // We are done, so we can exit
            return;
        }

        // The caret is not in this token, count its text length (adding one for the separator at
        // the end, which represents a space)
        currentCharOffset += currentTokenElement.textContent.length + 1;
    }

    // If we got here, then that means the desired caret position is past the end of the current
    // set of tokens. In this case, just put the caret in the last separatar after all the tokens.
    const lastTokenElement: Element = tokenElements[tokenElements.length - 1];
    moveCaretIntoNodeWithOffset(
        lastTokenElement,
        lastTokenElement.textContent.length,
    );
    moveCaretIntoNextNodeStart();
}

export function removeCursor() {
    (document.getSelection() as Selection).removeAllRanges();
}

export function moveCaretIntoFirstChildElement(container: Element) {
    const firstChild = container.children[0];
    if (!firstChild) {
        logger.warn(
            'moveCaretIntoFirstChildElement called with no children in container',
            container,
        );
        return;
    }

    moveCaretIntoStartOfNode(firstChild);
}

// Note that typically you want to call this with a #text node, and not with a regular HTMLElement
// node, since that results in the same result as if the user placed the caret through UI actions.
// If you want to place the caret at the end of an element, find the #text node inside that element
// and call this function with that #text node.
export function moveCursorToEndOfContainer(container: Element) {
    const range = document.createRange();
    range.selectNodeContents(container);
    range.collapse(false); // False means collapse to end

    const selection = document.getSelection() as Selection;
    selection.removeAllRanges();
    selection.addRange(range);
}

export function getNodePosition(
    container: Element,
    node: Element,
): TokenizerTokenPosition {
    const containerRect = container.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    const topCoord = nodeRect.top - containerRect.top;
    const leftCoord = nodeRect.left - containerRect.left;

    return {
        top: topCoord,
        left: leftCoord,
    };
}

/*
 * Check if the delete button (X) was clicked. The delete button is a pseudo-element, which is done
 * to reduce the number of elements in the DOM. Since psuedo-elements can't receive click events
 * directly, we have to do this calculation ourselves.
 */
export function isClickWithinDeleteButton(evt: MouseEvent): boolean {
    const targetBoundingRect = (evt.target as Element).getBoundingClientRect();
    return (
        evt.clientX >
            targetBoundingRect.right - TOKEN_DELETE_BUTTON_BOUNDS.left &&
        evt.clientX <
            targetBoundingRect.right + TOKEN_DELETE_BUTTON_BOUNDS.right &&
        evt.clientY <
            targetBoundingRect.top + TOKEN_DELETE_BUTTON_BOUNDS.bottom &&
        evt.clientY > targetBoundingRect.top - TOKEN_DELETE_BUTTON_BOUNDS.top
    );
}

/*
 * Check if the token context menu button was clicked. The context menu button is a pseudo-element,
 * which is done to reduce the number of elements in the DOM. Since psuedo-elements can't receive
 * click events directly, we have to do this calculation ourselves.
 */
export function isClickWithinContextMenuButton(evt: MouseEvent): boolean {
    const targetBoundingRect = (evt.target as Element).getBoundingClientRect();
    return (
        evt.clientX >
            targetBoundingRect.right - TOKEN_CONTEXT_MENU_BUTTON_BOUNDS.left &&
        evt.clientX <
            targetBoundingRect.right + TOKEN_CONTEXT_MENU_BUTTON_BOUNDS.right &&
        evt.clientY <
            targetBoundingRect.top + TOKEN_CONTEXT_MENU_BUTTON_BOUNDS.bottom &&
        evt.clientY >
            targetBoundingRect.top - TOKEN_CONTEXT_MENU_BUTTON_BOUNDS.top
    );
}

export function getInnerHtmlWithoutTransientClasses(container: HTMLElement) {
    const containerClone: HTMLElement = container.cloneNode(
        true,
    ) as HTMLElement;

    // Iterate through all child nodes and remove the transient CSS classes if present
    Array.from(containerClone.children).forEach(childNode => {
        childNode.classList.remove(classes.EDITED, classes.CONTEXT_MENU_OPENED);
    });

    // Reading innerHTML will return a string where special characters (&, <, >) are automatically
    // escaped. For example, if the HTML is "tax > 0", the innerHTML will return "tax &gt; 0",
    // which is not what we wanted. To work around this, we use a dummy textarea whose value field
    // gives us the raw unescaped string we want. See links below for more details.
    // See innerHTML spec: https://developer.mozilla.org/en-US/docs/Web/API/Element/innerHTML
    // See workaround details: https://stackoverflow.com/a/7394787/3808341
    const dummyTextarea = document.createElement('textarea');
    dummyTextarea.innerHTML = DOMPurify.sanitize(containerClone.innerHTML);
    return dummyTextarea.value;
}

export function getHtmlForTokens(
    tokens: TokenizerToken[],
    shouldEscapeTokenText = true,
    isTokenContextMenuEnabled = false,
): string {
    if (!tokens.length) {
        return initTokenTemplate;
    }

    return tokens
        .map((token: TokenizerToken) => {
            const text = shouldEscapeTokenText
                ? _.escape(token.text)
                : token.text;

            const cssClasses = cx(classes.TOKEN, token.cssClassNames, {
                [classes.IS_CONTEXT_MENU_ENABLED]: isTokenContextMenuEnabled,
                [classes.EXTENSIBLE]: token.isExtensible,
                [classes.INCOMPLETE]: token.isIncomplete,
            });

            const tokenHtml = getTokenTemplate(
                token.id,
                text,
                token.tooltip,
                cssClasses,
            );

            // Add a separator after the token
            // TODO(Rifdhan) do we need one at the very end?
            return tokenHtml + separatorTemplate;
        })
        .join('');
}

/*
 * Given a token element that is being deleted (also assume the separator after that token is also
 * being deleted), find the correct element to move the caret into after the deletion, assuming the
 * caret starts out inside the token or separator being deleted.
 */
export function getElementToMoveCaretIntoAfterTokenDeletion(
    container: HTMLElement,
    tokenBeingDeleted: HTMLElement,
): HTMLElement {
    const childElements = Array.from(container.children) as HTMLElement[];
    const deletedTokenIdx = childElements.findIndex(
        (child: HTMLElement) => child === tokenBeingDeleted,
    );

    // Some sanity checks - we should not be deleting the only token + separator pair, and the
    // token being deleted should have been a child of the container element
    if (childElements.length <= 2 || deletedTokenIdx < 0) {
        logger.error(
            'getElementToMoveCaretIntoAfterTokenDeletion called with too few child elements, or ' +
                'child element being deleted not within container element',
            container,
            tokenBeingDeleted,
        );
        return null;
    }

    // If we are deleting the very first token, the caret should end up in the second token. Since
    // there is a separator after each token, the second token is the third child element.
    if (deletedTokenIdx === 0) {
        return childElements[2];
    }

    // In any other case, the caret ends up in the separator immediately before the deleted token
    return childElements[deletedTokenIdx - 1];
}

/*
 * Looks through all children of the container, and performs two types of element normalization:
 * 1. If there is a separator with text in it, convert it to a full token with separators on either
 *    side of it.
 * 2. If there are consecutive empty elements (separators or tokens), replace the consecutive empty
 *    elements with a single empty separator.
 */
export function normalizeTokenAndSeparatorElements(
    container: HTMLElement,
    newTokenCssClassNames: string,
    onCaretNodeChanged: () => void,
) {
    let didEditElements = false;

    // Disable CSS animations while we modify the HTML, to avoid unexpected animations from us
    // changing elements around while the user types
    container.classList.add(classes.DISABLE_ANIMATIONS);

    // Check if the current element is a separator with text in it. If so, we will replace it with
    // a full token element, with separators between it and any surrounding tokens.
    for (let i = 0; i < container.children.length; i++) {
        const currentChild = container.children[i];
        if (
            currentChild.textContent.length > 0 &&
            (currentChild.classList.contains(classes.SEPARATOR) ||
                currentChild.classList.contains(classes.INIT_TOKEN))
        ) {
            // Get the HTML for the new token element we want to add. There will already be a
            // separator at the end of the output of getHtmlForTokens, but we also have to add a
            // separator before the token ourselves, if it is not the first token (no need for a
            // separator preceding the very first token).
            const addedPrecedingSeparator = i > 0;
            const newTokenHtml = DOMPurify.sanitize(
                (addedPrecedingSeparator ? separatorTemplate : '') +
                    getHtmlForTokens([
                        {
                            id: '',
                            text: currentChild.textContent,
                            tooltip: '',
                            cssClassNames: newTokenCssClassNames,
                            isIncomplete: true,
                            isExtensible: true,
                        },
                    ]),
            );

            // Add the new element after the existing separator element
            // eslint-disable-next-line no-unsanitized/method
            currentChild.insertAdjacentHTML('afterend', newTokenHtml);

            // Get a reference to the new token element (if we added a new separator immediately
            // before it, skip over that)
            const newTokenElement = addedPrecedingSeparator
                ? currentChild.nextElementSibling.nextElementSibling
                : currentChild.nextElementSibling;

            // Remove the old separator element and move the caret into the new token element
            currentChild.remove();
            moveCaretIntoNodeWithOffset(
                newTokenElement,
                newTokenElement.textContent.length,
            );
            didEditElements = true;
        }

        // Check if we have found two consecutive empty nodes. If so, remove both of them and
        // insert a new empty separator element in their place.
        if (
            i > 0 &&
            currentChild.textContent.length === 0 &&
            container.children[i - 1].textContent.length === 0
        ) {
            // Remove the first of the two empty nodes
            container.children[i - 1].remove();

            // Add a new empty separator after the second of the two empty nodes, and get a
            // reference to it
            currentChild.insertAdjacentHTML(
                'afterend',
                DOMPurify.sanitize(separatorTemplate),
            );
            const newSeparatorElement = currentChild.nextElementSibling;

            // Remove the second of the two empty nodes, and move the caret into the new separator
            currentChild.remove();
            moveCaretIntoStartOfNode(newSeparatorElement);

            // Since we removed an element, we must move back one index spot
            i--;
            didEditElements = true;
        }
    }

    // If we modified any elements, those modifications must have been in response to what the user
    // just typed, and so we likely modified the element the caret was on. Notify the caller of
    // this so it can take any action in response.
    if (didEditElements) {
        onCaretNodeChanged();
    }

    // Must re-enable animations after all pending changes are applied, or else the animations will
    // still be seen.
    setTimeout(() => container.classList.remove(classes.DISABLE_ANIMATIONS));
}

/*
 * Looks through the text of each token and identifies non-standard whitespace characters within
 * them. It will print warnings if anything unexpected is found, as an aid to debugging issues with
 * typing in sage. We also look for multiple consecutive spaces and leading/trailing spaces.
 */
export function checkForUnexpectedWhitespaces(tokens: TokenizerToken[]) {
    tokens.forEach(token => {
        if (
            // Check for Zero Width Space (Unicode U+200B)
            token.text.includes(String.fromCharCode(8203)) ||
            // Check if the number of whitespaces (according to Regex) is different from the number
            // of vanilla space characters, which would indicate non-standard whitespaces
            (token.text.match(SINGLE_WHITESPACE_REGEX) || []).length !==
                token.text.split(' ').length - 1
        ) {
            logger.warn(
                'Token',
                token,
                'contains unexpected whitespace character(s), which may cause issues in the sage bar',
            );
        }

        if (
            // Check for more than 1 consecutive space in the string
            token.text.includes('  ')
        ) {
            logger.warn(
                'Token',
                token,
                'contains multiple consecutive space characters, which may cause issues in the sage bar',
            );
        }

        if (
            // Check for a leading and/or trailing space in the string
            token.text.startsWith(' ') ||
            token.text.endsWith(' ')
        ) {
            logger.warn(
                'Token',
                token,
                'contains a leading and/or trailing space character, which may cause issues in the sage bar',
            );
        }
    });
}
